// @ts-nocheck
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays the navigation bar.
 */

class CustomHeader extends HTMLElement {
  constructor() {
    super();

    this.container = this;

    this.getHeaderHeight();

    let lastWidth = document.body.clientWidth;

    const ro = new ResizeObserver(
      debounce(entries => {
        // @ts-ignore
        const newWidth = entries[0].contentRect.width;

        if (newWidth !== lastWidth) {
          lastWidth = newWidth;
          this.getHeaderHeight();
        }
      }, 10)
    );

    ro.observe(document.body);

    if (this.dataset.hideOnScroll === 'true') {
      this.setupHideOnScroll();
    }
  }

  getHeaderHeight() {
    const height = this.container.offsetHeight;

    document.documentElement.style.setProperty(
        '--header-height',
        `${height}px`
    );
  }

  // Tracks a native `<dialog>` (cart, search, ...) so the header can stay
  // un-transformed for as long as it's open OR mid-transition. Needed
  // because dialog.js's closeDialog() calls the native `.close()` (which
  // clears `.open`) *before* the CSS close transition plays, so `.open`
  // alone isn't a reliable "still visually active" signal — the
  // transitionstart/end listeners cover that gap.
  #trackDialog(dialog, { onActive } = {}) {
    let transitioning = false;
    // Only the properties these dialogs' own open/close transitions
    // animate (see .cart-drawer__dialog / [open] in header-actions.liquid,
    // .search-modal__content / dialog-modal in base.css).
    const relevantProperties = ['opacity', 'transform', 'visibility'];

    dialog.addEventListener('transitionstart', event => {
      if (!relevantProperties.includes(event.propertyName)) return;
      transitioning = true;
      onActive?.();
    });

    const clearTransitioning = event => {
      if (!relevantProperties.includes(event.propertyName)) return;
      transitioning = false;
    };
    dialog.addEventListener('transitionend', clearTransitioning);
    dialog.addEventListener('transitioncancel', clearTransitioning);

    // Reveal immediately the moment it opens (native `open` attribute),
    // without waiting for a scroll or transition event.
    const observer = new MutationObserver(() => {
      if (dialog.open) onActive?.();
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    this.trackedDialogObservers ??= [];
    this.trackedDialogObservers.push(observer);

    return () => dialog.open || transitioning;
  }

  setupHideOnScroll() {
    // This element animates via `transform` (see base.css) for a
    // smoother, GPU-composited slide. Dialogs whose content lives inside
    // .custom-header (the cart drawer's `<dialog>`, via header-actions)
    // are `position: fixed` — once .custom-header has a transform, it
    // becomes their containing block, so they visually ride along with
    // .custom-header's translateY whenever the header hides, shifting
    // them (e.g. the cart's close button) out of reach.
    // `isHeaderOwnDrawerOpen()` below keeps the header un-transformed for
    // as long as any of these are open or mid-transition, so that shift
    // can never happen.
    this.hideOnScrollTarget = this;
    this.lastScrollY = window.scrollY;

    // Also reflected on <html> so other fixed/sticky elements (e.g. the
    // sticky filters bar on collection/search pages) can react to the
    // header's visibility without each needing their own scroll listener.
    const setHidden = hidden => {
      this.hideOnScrollTarget.classList.toggle('header-hidden', hidden);
      document.documentElement.classList.toggle('header-hidden', hidden);
    };

    const revealNow = () => {
      setHidden(false);
      this.lastScrollY = window.scrollY;
    };

    // Only the header's own drawers (menu, country selector) should keep
    // the header visible while open — their trigger lives inside the
    // header itself. Other drawers on the page (collapsibles, fabric
    // samples, etc.) also set a `drawer-open` attribute on <html>, but
    // opening one of those shouldn't reveal an already-hidden header.
    const headerDrawerSelector =
      'custom-drawer[data-trigger="menu-drawer-trigger"][drawer-open], ' +
      'custom-drawer[data-trigger="country-selector-drawer-trigger"][drawer-open]';

    // The search modal isn't inside .custom-header (it renders at the
    // body level), but opening it still locks scroll the same way the
    // cart does, which can fire a spurious 'scroll' event that
    // incorrectly hides the header mid-interaction. Tracking it here
    // keeps that from happening, same as the cart.
    const dialogSelectors = ['cart-drawer-component dialog', '#search-modal dialog'];
    const dialogActiveChecks = dialogSelectors
      .map(selector => document.querySelector(selector))
      .filter(Boolean)
      .map(dialog => this.#trackDialog(dialog, { onActive: revealNow }));

    const isHeaderOwnDrawerOpen = () =>
      document.querySelector(headerDrawerSelector) !== null || dialogActiveChecks.some(isActive => isActive());

    const updateHideOnScroll = () => {
      this.hideOnScrollTicking = false;

      // A drawer's own open/close mechanics (scroll-lock, etc.) can fire
      // scroll events that have nothing to do with the user scrolling the
      // page — never hide the header while one of its own drawers is open,
      // otherwise its own trigger becomes unreachable and the drawer ends
      // up misaligned.
      if (isHeaderOwnDrawerOpen()) {
        revealNow();
        return;
      }

      // Every OTHER drawer/popup on the page (product info, fabric
      // samples, the AI tag drawer, etc.) sets this same `drawer-open`
      // attribute and locks scroll too, which can fire that same kind of
      // spurious scroll event. Those shouldn't force-reveal an
      // already-hidden header (unlike the header's own drawers above,
      // whose trigger needs to stay reachable) - but they also shouldn't
      // let a fake scroll delta hide or reveal it either. Ignore the event
      // entirely rather than falling through to the real scroll-delta
      // logic below.
      if (document.documentElement.hasAttribute('drawer-open')) {
        return;
      }

      const currentScrollY = window.scrollY;
      const delta = currentScrollY - this.lastScrollY;

      if (currentScrollY <= this.offsetHeight) {
        setHidden(false);
      } else if (delta > 5) {
        setHidden(true);
      } else if (delta < -5) {
        setHidden(false);
      }

      this.lastScrollY = currentScrollY;
    };

    // Safari (mobile in particular) can fire several 'scroll' events per
    // rendered frame during momentum scrolling; toggling classes on every
    // one of them is what produced the animation "lag" — rAF collapses
    // that down to at most one update per frame.
    this.onHideOnScroll = () => {
      if (this.hideOnScrollTicking) return;
      this.hideOnScrollTicking = true;
      requestAnimationFrame(updateHideOnScroll);
    };

    window.addEventListener('scroll', this.onHideOnScroll, { passive: true });

    // Reveal immediately the moment a drawer opens, without waiting for a
    // scroll event to fire.
    this.drawerOpenObserver = new MutationObserver(() => {
      if (isHeaderOwnDrawerOpen()) revealNow();
    });

    this.drawerOpenObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['drawer-open'],
    });
  }

  disconnectedCallback() {
    if (this.onHideOnScroll) {
      window.removeEventListener('scroll', this.onHideOnScroll);
    }
    if (this.drawerOpenObserver) {
      this.drawerOpenObserver.disconnect();
    }
    if (this.trackedDialogObservers) {
      this.trackedDialogObservers.forEach(observer => observer.disconnect());
    }
  }
}

if (!customElements.get('custom-header')) {
  customElements.define('custom-header', CustomHeader);
}

(function() {
  if (!document.documentElement.classList.contains('overlay-header')) return;

  function updateHeader() {
    if (window.innerWidth > 959) {
      document.documentElement.classList.remove('overlay-header-top');
      return;
    }

    const announcementHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--announcement-bar-height')
    ) || 0;

    const atTop = window.scrollY <= announcementHeight;
    document.documentElement.classList.toggle('overlay-header-top', atTop);
  }

  updateHeader();
  window.addEventListener('scroll', updateHeader);
  window.addEventListener('resize', updateHeader);
})();
